import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, isFirebaseEnabled } from "../firebase/firebase";
import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  EmailAuthProvider,
  deleteUser,
  reauthenticateWithCredential,
  updatePassword
} from "firebase/auth";
import { getAuthErrorMessage } from '../utils/errorMessages';
import { clearCacheByPrefix, readCache, writeCache } from '../utils/cacheStorage';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [savedReplaysIds, setSavedReplaysIds] = useState([]);

  const signInWithGoogle = async () => {
    if (!isFirebaseEnabled || !auth) {
      const msg = 'Authentication is not available. Please try again later.';
      setAuthError(msg);
      throw new Error(msg);
    }
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      setAuthError(null);
      return result;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  };

  const signUpWithEmail = async (email, password, displayName) => {
    if (!isFirebaseEnabled || !auth) {
      const msg = 'Authentication is not available. Please try again later.';
      setAuthError(msg);
      throw new Error(msg);
    }
    try {
      if (password.length < 6) {
        // replicamos la forma de Firebase para que getAuthErrorMessage lo coja
        // eslint-disable-next-line no-throw-literal
        throw { code: 'auth/weak-password' };
      }
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName });
      setAuthError(null);
      return result;
    } catch (error) {
      const msg = getAuthErrorMessage(error);
      setAuthError(msg);
      // relanzamos un Error con nuestro mensaje en lugar del original
      throw new Error(msg);
    }
  };

  const signInWithEmail = async (email, password) => {
    if (!isFirebaseEnabled || !auth) {
      const msg = 'Authentication is not available. Please try again later.';
      setAuthError(msg);
      throw new Error(msg);
    }
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      setAuthError(null);
      return result;
    } catch (error) {
      setAuthError(getAuthErrorMessage(error));
      throw error;
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    if (!currentUser) throw new Error('No user logged in');
    
    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        oldPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, newPassword);
    } catch (error) {
      throw error;
    }
  };

  const deleteAccount = async (password = null) => {
    if (!currentUser) throw new Error('No user logged in');
    
    try {
      if (currentUser.providerData[0].providerId === 'password' && password) {
        const credential = EmailAuthProvider.credential(
          currentUser.email,
          password
        );
        await reauthenticateWithCredential(currentUser, credential);
      }
      localStorage.removeItem(`saved_games_${currentUser.uid}`);
      await deleteUser(currentUser);
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    localStorage.removeItem('analyticsReplays');
    return signOut(auth);
  };

  const clearSavedReplays = () => setSavedReplaysIds([]);

  const invalidatePublicGamesCache = () => {
    clearCacheByPrefix('public-games-cache:', {
      excludeKeys: ['public-games-cache:formats']
    });
  };

  useEffect(() => {
    if (!isFirebaseEnabled || !auth || typeof auth.onAuthStateChanged !== 'function') {
      // Firebase not configured in this environment (e.g., local dev without credentials)
      setLoading(false);
      return () => {};
    }

    const unsubscribe = auth.onAuthStateChanged(user => {
      if (!user) {
        localStorage.removeItem('analyticsReplays');
        setSavedReplaysIds([]);
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setSavedReplaysIds([]);
      return;
    }
    const key = `saved_games_${currentUser.uid}`;
    const cached = readCache(key);
    if (cached && Array.isArray(cached)) {
      setSavedReplaysIds(cached.map(g => g.replay_id));
    } else {
      setSavedReplaysIds([]);
    }
  }, [currentUser]);

  const save = async gameObj => {
    if (!currentUser) return;
    const key = `saved_games_${currentUser.uid}`;
    let games = readCache(key) || [];
    if (!Array.isArray(games)) games = [];
    
    // Prevent duplicate entries
    if (!games.find(g => g.replay_id === gameObj.replay_id)) {
      games.push(gameObj);
      writeCache(key, games);
    }
    
    setSavedReplaysIds(ids => {
      if (!ids.includes(gameObj.replay_id)) {
        return [...ids, gameObj.replay_id];
      }
      return ids;
    });
    invalidatePublicGamesCache();
  };

  const unsave = async replayId => {
    if (!currentUser) return;
    const key = `saved_games_${currentUser.uid}`;
    let games = readCache(key) || [];
    if (!Array.isArray(games)) games = [];
    
    games = games.filter(g => g.replay_id !== replayId);
    writeCache(key, games);

    setSavedReplaysIds(ids => ids.filter(id => id !== replayId));
    invalidatePublicGamesCache();
  };

  const value = {
    currentUser,
    savedReplaysIds,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    authError,
    changePassword,
    deleteAccount,
    save,
    unsave,
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword,
    clearSavedReplays
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}